import Vue from 'vue';
import type { ActionTree, GetterTree, MutationTree } from 'vuex';
import isEmpty from 'lodash/isEmpty';
import { buildPostsQuery, reorder, buildCommentsQuery, getCommentsSortingParams } from './helpers/warRoom';
import { withRollback } from '@/components/utils/store';
import { toCamelTransformator, toKebabTransformator } from '@/components/utils/core/object';
import { uniq } from '@/components/utils/core/array';
import {
  SUGGESTED_CATEGORY_TITLE,
  categoryData,
} from '@/components/partials/war-room/WarRoomPostForm/WarRoomPostFormCategories/constants';
import {
  calculateDownvote,
  calculateUpvote,
  isExtraRoleAdmin,
  isWarRoomUser,
} from '@/components/partials/war-room/utils';
import { votableTypes, warRoomStates } from '@/components/partials/war-room/constants';
import { addVoter } from '@/components/partials/war-room/WarRoomPostPollV2/utils';
import type { IPost } from '@/types/community';
import { abbreviateNumber } from '~/components/utils/core/number';
import {
  countCommentInTree,
  createCommentTree,
  isThreadSafeToDelete,
  searchCommentTreeById,
  sortCommentTree,
} from '~/components/utils/commentTree';
import type { ITopic } from '~/types/common';
import type { RootState } from '~/types/store/index';
import type { WarRoom, Response, OnboardingProfile } from '~/types/store/warRoom';

const getInitialState = (): WarRoom.State => ({
  post_dict: {},
  topic_dict: {},
  commentTree: [],
  user_dict: {},
  public_user_dict: {},
  post_slugs: [],
  posts_next_page: null,
  posts_previous_page: null,
  posts_total_pages: null,
  feedScrollMeta: {},
  pages: [],
  loading: false,
  loadingMore: false,
  profile: {},
  onboardingProfile: {},
  popular_topics: [],
  topics_with_posts: [],
  relatedPosts: {
    relatedPostScoreDict: {},
    posts: [],
  },
  commentsPagination: {
    comments_current_page: 0,
    comments_previous_page: 0,
    comments_next_page: 0,
    comments_total_pages: 0,
  },
  current_profile: {},
  unreadPostsCount: 0,
  isUnreadPostsLabelActive: true,
});

export const state = getInitialState;

export const getters: GetterTree<WarRoom.State, RootState> = {
  currentPost(state, getters, rootState) {
    const slug = rootState.route.params.postSlug || rootState.route.query.postSlug;
    const post: IPost | undefined = getters.postBySlug(slug);
    if (!post) return {};

    const extendedPost = {
      ...post,
      author: state.user_dict[post.user_username] || state.public_user_dict[post.user_slug],
    };

    if (typeof post.category === 'string') {
      extendedPost.category = categoryData[post.category];
    }

    return extendedPost;
  },
  postBySlug(state) {
    return (postSlug: string) => state.post_dict[postSlug];
  },
  posts(state) {
    return state.post_slugs
      .map(postSlug => {
        const post: IPost | undefined = state.post_dict[postSlug];
        if (!post) return null;

        const extendedPost = {
          ...post,
          topics: post.topic_ids.map(id => state.topic_dict[id]),
          author: state.user_dict[post.user_username] || state.public_user_dict[post.user_slug],
        };

        if (typeof post.category === 'string') {
          extendedPost.category = categoryData[post.category];
        }

        return extendedPost;
      })
      .filter(Boolean);
  },
  topics(state) {
    return Object.values(state.topic_dict);
  },
  topicsPublic(_state, getters) {
    return (getters.topics as ITopic[]).filter(({ category }) => category !== SUGGESTED_CATEGORY_TITLE);
  },
  topicBySlug(_, getters) {
    return (topicSlug: string) => (getters.topics as ITopic[]).find(({ slug }) => slug === topicSlug);
  },
  currentUserName(_state, _getters, rootState) {
    return rootState.route.params.username || (rootState.auth.user && rootState.auth.user.username);
  },
  currentUserProfile(state, getters) {
    return state.profile[getters.currentUserName];
  },
  currentUserTotalRevenue(state, getters) {
    return abbreviateNumber(getters.currentUserProfile?.deals.total_amount || 0);
  },
  currentUserDealsCount(state, getters) {
    return getters.currentUserProfile?.deals.total_count || 0;
  },
  currentUserAverageContractValue(state, getters) {
    const totalAmount = getters.currentUserProfile?.deals.total_amount || 0;
    const average = getters.currentUserDealsCount !== 0 ? totalAmount / getters.currentUserDealsCount : 0;

    return abbreviateNumber(average);
  },
  postsByUsername(_state, getters) {
    return (username: string) => (getters.posts as IPost[]).filter(p => p.user_username === username);
  },
  pagination(state) {
    return {
      nextPage: state.posts_next_page,
      previousPage: state.posts_previous_page,
      totalPages: state.posts_total_pages,
      loading: state.loadingMore,
    };
  },
  commentsPagination(state) {
    return {
      currentPage: state.commentsPagination.comments_current_page,
      nextPage: state.commentsPagination.comments_next_page,
      previousPage: state.commentsPagination.comments_previous_page,
      totalPages: state.commentsPagination.comments_total_pages,
      loading: false,
    };
  },
  isAdmin(state, getters, rootState) {
    return rootState.auth.loggedIn && rootState.auth.user.admin && isExtraRoleAdmin();
  },
  isWarRoomUser(state, getters, rootState) {
    return isWarRoomUser(rootState.auth.user);
  },
};

export const mutations: MutationTree<WarRoom.State> = {
  appendCommentTree(state, commentTree) {
    state.commentTree.push(...commentTree);
  },
  setCommentTree(state, commentTree = []) {
    state.commentTree = commentTree;
  },
  updatePost(state, { post, attributes }) {
    Vue.set(state.post_dict, post.slug, { ...state.post_dict[post.slug], ...attributes });
  },
  deletePost(state, { postSlug }) {
    const index = state.post_slugs.indexOf(postSlug);
    Vue.delete(state.post_slugs, index);
    Vue.delete(state.post_dict, postSlug);
  },
  updateComment(state, { comment, attributes }) {
    const commentToUpdate = searchCommentTreeById(state.commentTree, comment.id);

    if (commentToUpdate) {
      Object.assign(commentToUpdate, comment, attributes);
    }
  },
  deleteComment(state, { post, commentId }) {
    const commentToDelete = searchCommentTreeById(state.commentTree, Number(commentId));

    if (commentToDelete) {
      if (post && state.post_dict[post.slug]) {
        state.post_dict[post.slug].comment_count.total -= 1;
      }

      if (isThreadSafeToDelete(commentToDelete)) {
        if (commentToDelete.parent_id) {
          const parent = searchCommentTreeById(state.commentTree, commentToDelete.parent_id);

          if (parent) {
            const index = parent.replies!.indexOf(commentToDelete);

            parent.replies!.splice(index, 1);
          }
        } else {
          const index = state.commentTree.indexOf(commentToDelete);

          state.commentTree.splice(index, 1);
        }
      } else {
        commentToDelete.is_deleted = true;
      }
    }
  },
  hideComment(state, { post, commentId }) {
    const commentToDelete = searchCommentTreeById(state.commentTree, Number(commentId));

    if (commentToDelete) {
      if (post && state.post_dict[post.slug]) {
        state.post_dict[post.slug].comment_count.total -= countCommentInTree(commentToDelete);
      }

      if (commentToDelete.parent_id) {
        const parent = searchCommentTreeById(state.commentTree, commentToDelete.parent_id);

        if (parent) {
          const index = parent.replies!.indexOf(commentToDelete);

          parent.replies!.splice(index, 1);
        }
      } else {
        const index = state.commentTree.indexOf(commentToDelete);

        state.commentTree.splice(index, 1);
      }
    }
  },
  handleUpdatePost(state, { post, index }: { post: IPost; index: number }) {
    Vue.set(state.relatedPosts.posts, index, post);
  },
  updateState(state, newState: WarRoom.State) {
    Object.assign(state, newState);
  },
  mergeState(state, newState: WarRoom.State) {
    const stateKeys = ['post_dict', 'user_dict', 'public_user_dict'] as const;
    const mergeDict = (key: typeof stateKeys[number]) => ({ ...state[key], ...newState[key] });
    const mergedData = stateKeys.reduce((acc, key) => Object.assign(acc, { [key]: mergeDict(key) }), {});

    if (newState.post_slugs) {
      const post_slugs = [...new Set([...state.post_slugs, ...newState.post_slugs])];
      Object.assign(state, { post_slugs });
    }

    Object.assign(state, mergedData);
  },
  addComment(state, { post, comment, parentId, insertAt = 0 }) {
    comment.isNew = true;
    if (!comment.replies) comment.replies = [];

    if (post && state.post_dict[post.slug]) {
      state.post_dict[post.slug].comment_count.total += 1;
    }

    if (parentId) {
      const parentComment = searchCommentTreeById(state.commentTree, parentId);

      if (parentComment) {
        parentComment.replies!.splice(insertAt, 0, comment);
      }
    } else {
      state.commentTree.unshift(comment);
    }
  },
  addUsers(state, { userDict, publicUserDict }) {
    state.user_dict = { ...state.user_dict, ...userDict };
    state.public_user_dict = { ...state.public_user_dict, ...publicUserDict };
  },
  setRelatedPosts(state, { post_slugs, post_dict, topic_dict }) {
    state.relatedPosts.posts = post_slugs
      .map((postSlug: string) => {
        const post = post_dict[postSlug];

        if (!post) return null;

        post.topics = post.topic_ids.map((id: number) => topic_dict[id]);
        // @ts-ignore
        post.category = categoryData[post.category];

        // Note: remove content of post for related becuase we don't need that
        delete post.content.data.text;

        return post;
      })
      .filter(Boolean);
  },
  setRelatedPostScoreDict(state, { related_post_score_dict }) {
    state.relatedPosts.relatedPostScoreDict = related_post_score_dict || {};
  },
  addTopics(state, topics: ITopic[]) {
    const topicDict = topics.reduce((acct, topic) => Object.assign(acct, { [topic.id]: topic }), {});
    Object.assign(state.topic_dict, topicDict);
  },
  setFeedScrollMeta(state, payload) {
    state.feedScrollMeta = payload;
  },
};

export const actions: ActionTree<WarRoom.State, RootState> = {
  async fetchPost({ commit }, { postSlug }) {
    const { post_dict, topic_dict, user_dict, public_user_dict } = await this.$api.$get<Response.GetPost>(
      `/api/pages/war_room/posts/${postSlug}.json`,
    );

    commit('updateState', { topic_dict });
    commit('mergeState', { post_dict, user_dict, public_user_dict });
  },
  async fetchPosts({ state, commit }, { params, query, res }) {
    const postsParams = buildPostsQuery(params, query);

    commit('updateState', { loading: true });

    const { data, headers } = await this.$api.get<Response.GetPosts>(`/api/war_room/posts.json`, {
      params: postsParams,
    });

    // FIXME: do we still use it? move to nuxt server middleware
    if (process.server && res) {
      const nuxtHeaders = res.getHeaders();
      const newCookies = [...nuxtHeaders['set-cookie']];

      if (headers['set-cookie']) {
        newCookies.concat(headers['set-cookie']);
      }

      res.setHeader('set-cookie', newCookies);
    }

    const postsWithPage = reorder(data.post_dict, Number(query.page) || 1);
    const postDict = { ...state.post_dict, ...postsWithPage };

    commit('updateState', {
      ...data,
      post_dict: postDict,
      pages: [Number(query.page)],
    });
    commit('updateState', { loading: false });
  },
  async fetchMorePosts({ state, commit }, { params, query, direction }) {
    const postsParams = buildPostsQuery(params, query);

    const page = Number(query.page) || 1;

    commit('updateState', { loadingMore: true });

    const data = await this.$api.$get<Response.GetPosts>(`/api/war_room/posts.json`, { params: postsParams });

    const stateKeys = ['post_dict', 'post_slugs', 'topic_dict'] as const;
    const mergedState = stateKeys.reduce<Record<string, object>>((acct, key) => {
      const prepared = key === 'post_dict' ? reorder(data[key], page) : data[key];

      acct[key] = Array.isArray(data[key])
        ? direction === 'up'
          ? [...(prepared as string[]), ...(state[key] as string[])]
          : [...(state[key] as string[]), ...(prepared as string[])]
        : { ...state[key], ...prepared };

      return acct;
    }, {});

    commit('updateState', { ...data, ...mergedState });
    commit('updateState', { loadingMore: false });
  },
  async fetchRelatedPosts({ commit }, { postSlug }) {
    const { post_slugs, post_dict, topic_dict, related_post_score_dict } = await this.$api.$get(
      `/api/pages/war_room/posts/${postSlug}/related_posts.json`,
    );

    commit('setRelatedPostScoreDict', { related_post_score_dict });
    commit('setRelatedPosts', { post_slugs, post_dict, topic_dict });
  },
  async fetchPostComments({ commit }, { postSlug, query, page, commentsMutation = 'appendCommentTree' }) {
    const commentsQuery = buildCommentsQuery(query, page);
    const { sortBy, sortOrder } = getCommentsSortingParams(query);

    const {
      comment_dict,
      user_dict,
      public_user_dict,
      comments_current_page,
      comments_next_page,
      comments_previous_page,
      comments_total_pages,
    } = await this.$api.$get<Response.GetComments>(`/api/war_room/posts/${postSlug}/comments`, {
      params: commentsQuery,
    });

    const commentTree = createCommentTree(comment_dict);
    sortCommentTree(commentTree, sortBy, sortOrder);

    commit(commentsMutation, commentTree);
    commit('mergeState', { user_dict, public_user_dict });
    commit('updateState', {
      commentsPagination: {
        comments_current_page,
        comments_next_page,
        comments_previous_page,
        comments_total_pages,
      },
    });
  },
  async fetchCommentsSubtree({ commit }, { postSlug, query, commentId }) {
    const { comment_dict, user_dict, public_user_dict } = await this.$api.$get<Response.GetComments>(
      `/api/war_room/posts/${postSlug}/comments/${commentId}/subtree.json`,
    );
    const { sortBy, sortOrder } = getCommentsSortingParams(query);

    const commentTree = createCommentTree(comment_dict);
    sortCommentTree(commentTree, sortBy, sortOrder);

    commit('setCommentTree', commentTree);
    commit('mergeState', { user_dict, public_user_dict });
  },
  async fetchSidebar({ commit }) {
    const sidebar = await this.$api.$get<Response.GetFeedSidebar>('/api/pages/war_room/posts.json');

    commit('updateState', sidebar);
  },
  async fetchTopics({ commit, state }) {
    if (isEmpty(state.post_dict)) {
      const { topics } = await this.$api.$get<Response.GetTopics>('/api/war_room/topics.json');
      commit('addTopics', topics);
    }
  },
  addVote: withRollback(async function addVoteAction({ commit }, { votable, type, value }) {
    const { nextRating } = calculateUpvote(votable, value);
    const attributes = { rating: nextRating, current_vote: value };

    if (type === votableTypes.POST) {
      commit('updatePost', { post: votable, attributes });
    } else {
      commit('updateComment', { comment: votable, attributes });
    }

    await this.$api.$post('/api/war_room/ratings.json', {
      reactable_type: type,
      reactable_id: votable.id,
      value,
    });
  }),
  removeVote: withRollback(async function removeVoteAction({ commit }, { votable, type }) {
    const { nextRating } = calculateDownvote(votable);
    const attributes = { rating: nextRating, current_vote: 0 };

    if (type === votableTypes.POST) {
      commit('updatePost', { post: votable, attributes });
    } else {
      commit('updateComment', { comment: votable, attributes });
    }

    await this.$api.$delete(`/api/war_room/ratings?reactable_type=${type}&reactable_id=${votable.id}`);
  }),
  addPollChoice: withRollback(async function addPollChoiceAction(
    { commit, getters, rootState },
    { postSlug, choiceId },
  ) {
    const post: IPost | undefined = getters.postBySlug(postSlug);

    if (!(post && post.poll)) return;

    const updatedPoll = addVoter({ poll: post.poll, user: rootState.auth.user, choiceId });

    commit('updatePost', {
      post,
      attributes: {
        poll: updatedPoll,
      },
    });

    await this.$api.post(`/api/war_room/poll_votes.json`, {
      poll_vote: {
        choice_id: choiceId,
      },
    });
  }),
  deletePost: withRollback(async function deletePostAction({ commit }, { post }) {
    commit('deletePost', { postSlug: post.slug });

    await this.$api.$delete(`/api/war_room/posts/${post.slug}.json`);
  }),
  updatePostStats: withRollback(async function updatePostStatsAction({ commit, dispatch }, { post, attributes }) {
    commit('updatePost', { post, attributes });
    await Promise.all([
      this.$api.$put(`/api/war_room/admin/posts/${post.slug}.json`, { post: attributes }),
      dispatch('fetchPosts', { params: { slug: undefined }, query: {}, name: undefined }),
    ]);
  }),
  addMassUpvote: withRollback(async function updatePostStatsAction({ commit, dispatch }, { post, attributes }) {
    commit('updatePost', { post, attributes });
    await Promise.all([
      this.$api.$post(`/api/war_room/admin/posts/${post.slug}/mass_upvote.json`, { post: attributes }),
      dispatch('fetchPosts', { params: { slug: undefined }, query: {}, name: undefined }),
    ]);
  }),
  commentMassDownVote: withRollback(async function updateCommentStatsAction({ commit }, { comment, attributes }) {
    await this.$api.$post(`/api/admin/comments/${comment.id}/mass_downvote.json`, attributes);
    commit('updateComment', { comment: { ...comment, rating: comment.rating - attributes.downvotes_count } });
  }),
  async fetchProfile({ state, commit }, { username }) {
    const { posts, ...profile } = await this.$api.$get<Response.GetWarRoomProfile>(
      `/api/war_room/users/show.json?username=${username}`,
    );
    const updatedState = (Object.keys(posts) as Array<keyof typeof posts>).reduce<Record<string, object>>(
      (acct, key) => {
        const postsProp = posts[key];
        acct[key] = Array.isArray(postsProp)
          ? uniq([...(state[key] as string[]), ...postsProp])
          : { ...state[key], ...postsProp };
        return acct;
      },
      {},
    );
    commit('updateState', { ...updatedState, profile: { ...state.profile, [username]: profile } });
  },
  async fetchOnboardingProfile({ commit }) {
    const {
      username,
      warRoomAvatarUrl,
      warRoomPosition,
      warRoomYearsOfExperience,
      warRoomCustomExpectation,
      warRoomSalesRoles,
      warRoomTopics,
      warRoomExpectations,
      departments,
      productTypes,
      warRoomIndustries,
      warRoomCustomerSegments,
      warRoomCompanyStages,
      warRoomJobSearchStatus,
      warRoomIsInterestingToJobSurvey,
    } = await this.$api.$get<OnboardingProfile>('/api/war_room/user.json', {
      // @ts-ignore
      transformResponse: [...this.$api.defaults.transformResponse, toCamelTransformator],
    });
    commit('updateState', {
      onboardingProfile: {
        username,
        warRoomAvatarUrl,
        warRoomPosition,
        warRoomYearsOfExperience,
        warRoomCustomExpectation,
        warRoomSalesRoles,
        warRoomTopics,
        warRoomExpectations,
        departments,
        productTypes,
        warRoomIndustries,
        warRoomCustomerSegments,
        warRoomCompanyStages,
        warRoomJobSearchStatus,
        warRoomIsInterestingToJobSurvey,
      },
    });
  },
  async updateOnboardingProfile(_, data) {
    await this.$api.$put('/api/war_room/user.json', {
      user: toKebabTransformator(data),
    });
  },
  async finishOnboarding() {
    if (this.$auth.user!.warRoomState === warRoomStates.IN_ONBOARDING) {
      await this.$api.$post('/api/war_room/user/finish_onboarding.json');
    }

    this.$analytics.logEvent('onboarding__finish');

    await this.$auth.fetchUser();
  },
  async hideComment({ commit, getters }, { commentId, hidden_from_feed, text }) {
    await this.$api.$put(`/api/war_room/comments/${commentId}.json`, {
      comment: { hidden_from_feed, text },
    });

    // remove comment from UI
    commit('hideComment', { post: getters.currentPost, commentId });
  },
  async updateComment({ commit }, { commentId, text, media }) {
    const { comment_dict } = await this.$api.$put(`/api/war_room/comments/${commentId}.json`, {
      comment: { text, media },
    });
    const updatedComment = comment_dict[commentId];

    commit('updateComment', { comment: updatedComment });
  },
  async removeComment({ commit, getters }, { commentId }) {
    commit('deleteComment', { post: getters.currentPost, commentId });

    await this.$api.$delete(`/api/war_room/comments/${commentId}.json`);
  },
  toggleUnreadPostsLabel({ commit }, isUnreadPostsLabelActive) {
    commit('updateState', { isUnreadPostsLabelActive });
  },
  updateUnreadPostsCount({ state, commit }, unreadPostsCount) {
    if (state.unreadPostsCount !== unreadPostsCount) {
      commit('updateState', {
        unreadPostsCount,
        isUnreadPostsLabelActive: true,
      });
    }
  },
};
